/**
 * PresencePill — multi-operator soft awareness chip.
 *
 * Renders on draft cards when ≥1 OTHER operator (self is filtered out)
 * has the same draft open. Example:
 *
 *   ● kate reviewing · 14s ago
 *
 * Implementation:
 *   1. On mount: GET /admin/presence/{type}/{id} for initial state.
 *   2. Subscribe to assayerai:presence:{type}:{id} via useWebSocket.
 *   3. On every presence_changed broadcast, replace local operator list.
 *   4. Filter out self (current operator's email/uid).
 *   5. Render the first remaining operator's display + the relative
 *      `last_heartbeat_at`. If more than one other operator is present
 *      the count rolls up ("kate +1 reviewing · 5s ago").
 *
 * Plan reference: docs/wip/assayer-stream/MULTI_OPERATOR_CONCURRENCY_PLAN.md (Layer 3)
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import {
  listPresence,
  type PresenceOperator,
  type PresenceResourceType,
} from '../lib/api/presence.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { useAuth } from '../contexts/AuthContext.js';

interface PresencePillProps {
  resourceType: PresenceResourceType;
  resourceId: string | number | null | undefined;
  /** Tighten chip styling for compact list rows. Default false (drawer-sized). */
  compact?: boolean;
}

export function PresencePill({ resourceType, resourceId, compact = false }: PresencePillProps) {
  const { user, operator } = useAuth();
  // audit_identity convention: email when available, uid otherwise.
  // Mirrors the backend filter so "self" is correctly excluded regardless
  // of which form the row carries.
  const selfId = user?.email ?? user?.uid ?? operator?.email ?? null;

  const [operators, setOperators] = useState<PresenceOperator[]>([]);
  const [now, setNow] = useState(Date.now());

  // Initial state — one round-trip when the pill mounts. After that,
  // updates come exclusively via the WS channel below.
  const { data } = useQuery({
    queryKey: ['presence', resourceType, resourceId],
    queryFn: () => listPresence(resourceType, resourceId!),
    enabled: resourceId !== null && resourceId !== undefined,
    staleTime: 30_000,
  });
  useEffect(() => {
    if (data?.operators) setOperators(data.operators);
  }, [data]);

  // Per-resource channel — only subscribed while this pill is mounted,
  // so two cards on the same page don't share each other's broadcasts.
  const channel =
    resourceId !== null && resourceId !== undefined
      ? `assayerai:presence:${resourceType}:${resourceId}`
      : null;
  useWebSocket({
    channels: channel ? [channel] : [],
    onEvent: {
      presence_changed: (payload: { operators?: PresenceOperator[] }) => {
        if (Array.isArray(payload?.operators)) setOperators(payload.operators);
      },
    },
  });

  // Tick `now` once per second so the "Xs ago" relative time keeps
  // updating without a re-broadcast. Cheap — re-renders only the pill.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Filter self out — the operator should never see their own chip.
  const others = operators.filter(o => o.operator_id !== selfId);
  if (others.length === 0) return null;

  const lead = others[0];
  const extraCount = others.length - 1;
  const ago = formatAgo(lead.last_heartbeat_at, now);

  const label = lead.operator_display ?? lead.operator_id.split('@')[0];
  const text = extraCount > 0 ? `${label} +${extraCount} reviewing` : `${label} reviewing`;

  const sizing = compact ? 'text-[10px] px-1.5 py-0.5 gap-1' : 'text-xs px-2 py-1 gap-1.5';

  return (
    <span
      title={others.map(o => o.operator_display ?? o.operator_id).join(', ')}
      className={`inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 ${sizing}`}
    >
      <Eye className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      <span className="font-medium">{text}</span>
      {ago && <span className="opacity-70">· {ago}</span>}
    </span>
  );
}

function formatAgo(iso: string | null, nowMs: number): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const deltaSec = Math.max(0, Math.round((nowMs - t) / 1000));
  if (deltaSec < 5) return 'now';
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const m = Math.floor(deltaSec / 60);
  return `${m}m ago`;
}
